import { ApiService } from 'networking/api-service';
import { API_ROUTES } from 'networking/api-routes';
import { ApiErrorArray } from 'models/api-errors-array';

// Sharing a vault, an album or a single memory (api/app/services/sharing).

export type ShareTargetType = 'vault' | 'album' | 'memory';

export type ShareRole =
  | 'owner'
  | 'editor'
  | 'contributor'
  | 'limited_contributor'
  | 'viewer';

export type ShareStatus =
  | 'invited'
  | 'active'
  | 'declined'
  | 'removed'
  | 'expired';

/** What is shared: the vault, one of its albums, or one memory. */
export type ShareTarget = {
  type: ShareTargetType;
  vaultId: number;
  /** The album's or memory's id; the vault's for a vault. */
  id: number;
  name: string;
  isPrivate?: boolean;
};

export type ShareEntry = {
  id: string;
  kind: 'invitation' | 'token' | 'owner';
  name: string | null;
  email: string | null;
  userId: number | null;
  role: ShareRole;
  resource: {
    type: ShareTargetType;
    id: number;
    name: string | null;
    albumId?: number | null;
  };
  status: ShareStatus;
  invitedAt: string | null;
  invitedBy: { id: number; name: string } | null;
  expiresOn: string | null;
  /** Reaches it through the whole vault; shown, not changed, on an album. */
  inherited: boolean;
  canManage: boolean;
};

export type ShareLink = { enabled: boolean; url: string | null };

export type Invitation = {
  email: string;
  name: string | null;
  role: ShareRole;
  status: ShareStatus;
  resource: ShareEntry['resource'];
  vaultId: number;
  vaultName: string;
  inviterName: string;
  hasAccount: boolean;
  landingPath: string;
};

/** The viewer's own access, as the API reports it on vaults and albums. */
export type MyVaultAccess = {
  role: ShareRole | null;
  fullAccess: boolean;
  canManageVault: boolean;
  canManageBilling: boolean;
  canShareVault: boolean;
  canCreateAlbum: boolean;
  albumRoles: Record<string, ShareRole | null>;
  sharedContentIds: number[];
};

export type MyAlbumAccess = {
  role: ShareRole | null;
  canUpload: boolean;
  canCreatePrompt: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReorder: boolean;
  canShare: boolean;
  seesPrivate: boolean;
  /** They created it. */
  mine: boolean;
};

export const PRIVATE_SHARE_ERROR = 'resource_private';

const mapEntry = (raw: any): ShareEntry => ({
  id: String(raw.id),
  kind: raw.kind,
  name: raw.name ?? null,
  email: raw.email ?? null,
  userId: raw.user_id ?? null,
  role: raw.role,
  resource: {
    type: raw.resource?.type,
    id: raw.resource?.id,
    name: raw.resource?.name ?? null,
    albumId: raw.resource?.album_id ?? null,
  },
  status: raw.status,
  invitedAt: raw.invited_at ?? null,
  invitedBy: raw.invited_by ?? null,
  expiresOn: raw.expires_on ?? null,
  inherited: Boolean(raw.inherited),
  canManage: Boolean(raw.can_manage),
});

const mapInvitation = (raw: any): Invitation => ({
  email: raw.email,
  name: raw.name ?? null,
  role: raw.role,
  status: raw.status,
  resource: {
    type: raw.resource?.type,
    id: raw.resource?.id,
    name: raw.resource?.name ?? null,
    albumId: raw.resource?.album_id ?? null,
  },
  vaultId: raw.vault_id,
  vaultName: raw.vault_name,
  inviterName: raw.inviter_name,
  hasAccount: Boolean(raw.has_account),
  landingPath: raw.landing_path,
});

export const mapMyVaultAccess = (raw: any): MyVaultAccess | undefined =>
  raw
    ? {
        role: raw.role ?? null,
        fullAccess: Boolean(raw.full_access),
        canManageVault: Boolean(raw.can_manage_vault),
        canManageBilling: Boolean(raw.can_manage_billing),
        canShareVault: Boolean(raw.can_share_vault),
        canCreateAlbum: Boolean(raw.can_create_album),
        albumRoles: raw.album_roles || {},
        sharedContentIds: raw.shared_content_ids || [],
      }
    : undefined;

export const mapMyAlbumAccess = (raw: any): MyAlbumAccess | undefined =>
  raw
    ? {
        role: raw.role ?? null,
        canUpload: Boolean(raw.can_upload),
        canCreatePrompt: Boolean(raw.can_create_prompt),
        canEdit: Boolean(raw.can_edit),
        canDelete: Boolean(raw.can_delete),
        canReorder: Boolean(raw.can_reorder),
        canShare: Boolean(raw.can_share),
        seesPrivate: Boolean(raw.sees_private),
        mine: Boolean(raw.mine),
      }
    : undefined;

// The API's way of naming the target: vault_id plus album_id or content_id.
const targetParams = (target: ShareTarget) => ({
  vault_id: target.vaultId,
  ...(target.type === 'album' ? { album_id: target.id } : {}),
  ...(target.type === 'memory' ? { content_id: target.id } : {}),
});

/** The API error's `details`, e.g. PRIVATE_SHARE_ERROR or "wrong_account". */
export const shareErrorDetails = (error: unknown): string | null => {
  if (error instanceof ApiErrorArray) {
    return (error.errors[0]?.details as string) || null;
  }
  return null;
};

class SharesController {
  static async list(
    target: ShareTarget,
  ): Promise<{ shares: ShareEntry[]; link: ShareLink }> {
    const response = await ApiService.get<any>(
      API_ROUTES.SHARES,
      targetParams(target),
    );
    return {
      shares: (response.data.shares || []).map(mapEntry),
      link: response.data.link,
    };
  }

  static async invite(
    target: ShareTarget,
    params: {
      role: ShareRole;
      invitees: { email: string; name?: string }[];
      note?: string;
    },
  ): Promise<ShareEntry[]> {
    const response = await ApiService.post<any>(API_ROUTES.SHARES, {
      ...targetParams(target),
      ...params,
    });
    return (response.data.shares || []).map(mapEntry);
  }

  static async changeRole(id: string, role: ShareRole): Promise<ShareEntry> {
    const response = await ApiService.patch<any>(API_ROUTES.SHARE(id), {
      role,
    });
    return mapEntry(response.data);
  }

  static async remove(id: string): Promise<void> {
    await ApiService.delete(API_ROUTES.SHARE(id));
  }

  static async resend(id: string): Promise<void> {
    await ApiService.post(API_ROUTES.SHARE_RESEND(id));
  }

  static async updateLink(
    target: ShareTarget,
    change: { enabled?: boolean; regenerate?: boolean },
  ): Promise<ShareLink> {
    const response = await ApiService.patch<ShareLink>(API_ROUTES.SHARE_LINK, {
      ...targetParams(target),
      ...change,
    });
    return response.data;
  }

  static async getInvitation(token: string): Promise<Invitation> {
    const response = await ApiService.get<any>(API_ROUTES.INVITATION(token));
    return mapInvitation(response.data);
  }

  static async acceptInvitation(token: string): Promise<Invitation> {
    const response = await ApiService.post<any>(
      API_ROUTES.INVITATION_ACCEPT(token),
    );
    return mapInvitation(response.data);
  }

  static async declineInvitation(token: string): Promise<Invitation> {
    const response = await ApiService.post<any>(
      API_ROUTES.INVITATION_DECLINE(token),
    );
    return mapInvitation(response.data);
  }
}

export { SharesController };
